import { hasRuntimeObjectType, isStringValue } from './validation/primitives';

// SAFETY: The surrounding typed contract establishes the asserted representation.
export const TRANSCRIPTION_ACTIVE_STATUSES = [
  'pending',
  'processing',
  'cancellation_requested',
  // Free-tier file held for a concurrency slot; the waiting-slot promoter starts it.
  // Active (not terminal) so the UI keeps polling and the user can cancel it, but it
  // is NOT counted by getActiveFreeJobsCount, so waiting rows never block themselves.
  'waiting',
] as const;

// SAFETY: The surrounding typed contract establishes the asserted representation.
export const TRANSCRIPTION_TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'] as const;

// SAFETY: The surrounding typed contract establishes the asserted representation.
export const TRANSCRIPTION_STATUSES = [
  ...TRANSCRIPTION_ACTIVE_STATUSES,
  ...TRANSCRIPTION_TERMINAL_STATUSES,
] as const;

export type TranscriptionStatus = (typeof TRANSCRIPTION_STATUSES)[number];

export function isActiveTranscriptionStatus(
  status: string | null | undefined
): status is (typeof TRANSCRIPTION_ACTIVE_STATUSES)[number] {
  return (
    status === 'pending' ||
    status === 'processing' ||
    status === 'cancellation_requested' ||
    status === 'waiting'
  );
}

export function isTerminalTranscriptionStatus(
  status: string | null | undefined
): status is (typeof TRANSCRIPTION_TERMINAL_STATUSES)[number] {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export function isCancellableTranscriptionStatus(status: string | null | undefined): boolean {
  return (
    status === 'pending' ||
    status === 'processing' ||
    status === 'cancellation_requested' ||
    status === 'waiting'
  );
}

export function isBridgeBackedMetadata(
  metadata: Record<string, unknown> | null | undefined
): boolean {
  return isStringValue(metadata?.bridgeJobId) && metadata.bridgeJobId.length > 0;
}

/**
 * "No speech" is stored as status='failed' with metadata.outcome='no_speech'
 * (the bridge ran fine, the audio just had no transcribable speech — see
 * finalizeNoSpeech in bridge-completion-poller). It's NOT a real failure, so the
 * UI labels it distinctly (amber "No speech") instead of a red "Failed".
 */
export function isNoSpeechTranscription(t: {
  status?: string | null;
  metadata?: unknown;
}): boolean {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return (
    t.status === 'failed' &&
    (t.metadata as { outcome?: string } | null | undefined)?.outcome === 'no_speech'
  );
}

/**
 * Capacity / admission rejections (free-tier concurrency cap, queue full, wait
 * too long) are stored as status='failed' with metadata.outcome='blocked' — the
 * job never ran and nothing broke; the user just hit a limit and should wait or
 * retry. Like no_speech, it's NOT a real failure: the UI shows it amber
 * ("Limit reached") and the failure-rate metric excludes it. We also honour the
 * legacy `metadata.gate='concurrent'` marker written before `outcome` existed.
 */
export function isBlockedTranscription(t: { status?: string | null; metadata?: unknown }): boolean {
  if (t.status !== 'failed') return false;
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const meta = t.metadata as { outcome?: string; gate?: string } | null | undefined;

  return meta?.outcome === 'blocked' || meta?.gate === 'concurrent';
}

/**
 * The single source of truth for "did this transcription actually fail?" — a red
 * "Failed" the user/admin should worry about. Excludes the two mislabelled-as-
 * failed buckets (no_speech, blocked/capacity). Use this for failure-rate
 * metrics instead of a raw `status='failed'` count, which over-reports by ~3–4×.
 */
export function isRealFailure(t: { status?: string | null; metadata?: unknown }): boolean {
  return t.status === 'failed' && !isNoSpeechTranscription(t) && !isBlockedTranscription(t);
}

function metadataRecord(metadata: unknown): Record<string, unknown> {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return metadata && hasRuntimeObjectType(metadata) && !Array.isArray(metadata)
    ? (metadata as Record<string, unknown>)
    : {};
}

function isMeetingCaptureRow(t: { status?: string | null; metadata?: unknown }): boolean {
  const metadata = metadataRecord(t.metadata);

  return (
    metadata.source === 'meeting-capture' ||
    metadata.sourceLabel === 'Meeting' ||
    metadata.meetingCapture === true ||
    isStringValue(metadata.meetingSessionId)
  );
}

/** Pending upload that never reached enqueue (no provider, no queuedAt). */
export function isAwaitingStart(t: {
  status?: string | null;
  provider?: string | null;
  webhookJobId?: string | null;
  metadata?: unknown;
}): boolean {
  const meta = metadataRecord(t.metadata);

  return (
    t.status === 'pending' &&
    t.provider == null &&
    t.webhookJobId == null &&
    meta.queuedAt == null &&
    !isCreatorIntakeDownloading(t) &&
    !isMeetingCaptureRow(t)
  );
}

/**
 * Signed-in URL intake creates the transcription row before the async
 * creator-intake worker has downloaded and attached the media.  That row is
 * pending, but it is not waiting for the user to choose a mode or press Start:
 * the worker will hand it to the transcription queue automatically.
 */
export function isCreatorIntakeDownloading(t: {
  status?: string | null;
  metadata?: unknown;
}): boolean {
  const meta = metadataRecord(t.metadata);

  return (
    t.status === 'pending' &&
    meta.source === 'creator-intake' &&
    meta.intakeStatus === 'downloading'
  );
}

function isPendingQueued(t: {
  status?: string | null;
  provider?: string | null;
  webhookJobId?: string | null;
  metadata?: unknown;
}): boolean {
  const meta = metadataRecord(t.metadata);

  return (
    t.status === 'pending' &&
    (t.provider != null || t.webhookJobId != null || meta.queuedAt != null)
  );
}

function meetingCaptureBadge(t: {
  status?: string | null;
  metadata?: unknown;
}): { label: string; variant: TranscriptionBadgeVariant } | null {
  if (!isMeetingCaptureRow(t)) return null;

  const metadata = metadataRecord(t.metadata);

  if (t.status === 'completed') return { label: 'Ready', variant: 'completed' };

  if (t.status === 'processing') return { label: 'Transcribing', variant: 'processing' };

  if (t.status === 'failed') return { label: 'Failed', variant: 'failed' };

  const stage = isStringValue(metadata.stage) ? metadata.stage : null;

  if (stage === 'recording') return { label: 'Recording', variant: 'processing' };

  if (stage === 'partial_recording_ready') return { label: 'Partial', variant: 'warning' };

  // recording_ready = finalize done + enqueued, but NOT yet picked up by a worker
  // (status is still 'pending' until then). Label it "Queued", not "Transcribing" —
  // "Transcribing" is reserved for status='processing' above. A row whose enqueue
  // failed (orphan) honestly reads "Queued" until the orphan-recovery reaper moves it.
  if (stage === 'recording_ready') return { label: 'Queued', variant: 'pending' };

  return { label: 'Saving', variant: 'pending' };
}

export type TranscriptionBadgeVariant =
  | 'completed'
  | 'processing'
  | 'failed'
  | 'pending'
  | 'warning';

/** Shared status → badge mapping so the user list + admin render consistently. */
export function transcriptionBadge(t: {
  status?: string | null;
  provider?: string | null;
  webhookJobId?: string | null;
  metadata?: unknown;
}): { label: string; variant: TranscriptionBadgeVariant } {
  if (isNoSpeechTranscription(t)) return { label: 'No speech', variant: 'warning' };

  if (isBlockedTranscription(t)) return { label: 'Limit reached', variant: 'warning' };
  const meetingBadge = meetingCaptureBadge(t);

  if (meetingBadge) return meetingBadge;

  if (isCreatorIntakeDownloading(t)) return { label: 'Preparing', variant: 'processing' };

  if (isAwaitingStart(t)) return { label: 'Awaiting start', variant: 'warning' };

  if (isPendingQueued(t)) return { label: 'Queued', variant: 'pending' };

  switch (t.status) {
    case 'completed':
      return { label: 'completed', variant: 'completed' };
    case 'processing':
      return { label: 'processing', variant: 'processing' };
    case 'failed':
      return { label: 'failed', variant: 'failed' };
    case 'cancellation_requested':
      return { label: 'cancelling', variant: 'processing' };
    case 'waiting':
      // Held for a free-tier slot; starts automatically. Not an error, not a limit hit.
      return { label: 'Waiting for slot', variant: 'pending' };
    case 'cancelled':
      return { label: 'cancelled', variant: 'failed' };
    default:
      return { label: t.status ?? 'pending', variant: 'pending' };
  }
}
